import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import EmailIcon from "@redotech/redo-icons/mail-01.svg";
import ChatIcon from "@redotech/redo-icons/message-chat-circle.svg";
import MessageTextCircleIcon from "@redotech/redo-icons/message-text-circle-02.svg";
import MessageTextSquare02 from "@redotech/redo-icons/message-text-square-02.svg";
import PhoneIcon from "@redotech/redo-icons/phone.svg";
import PostscriptLogoIcon from "@redotech/redo-icons/postscript-logo.svg";
import FacebookIcon from "@redotech/redo-icons/social-icon-facebook.svg";
import InstagramIcon from "@redotech/redo-icons/social-icon-instagram.svg";
import TikTokIcon from "@redotech/redo-icons/social-icon-tiktok.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { AttentiveIntegrationPurpose } from "@redotech/redo-model/attentive-integration";
import { ConversationPlatform } from "@redotech/redo-model/conversation";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import AttentiveLogoIcon from "@redotech/redo-web/icon-old/attentive-logo.svg";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAttentiveIntegration } from "../client/attentive";
import { getFacebookIntegrations } from "../client/facebook";
import { getInstagramIntegrations } from "../client/instagram";
import { getPostscriptIntegration } from "../client/postscript";
import { getTiktokIntegration } from "../client/tiktok";
import { EmailIntegrationsContext } from "../services/email-integrations";
import * as supportOnboardingBannerCss from "./support-onboarding-banner.module.css";

interface ChannelConfig {
  platform: ConversationPlatform;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  route: string;
}

const CHANNEL_CONFIGS: ChannelConfig[] = [
  {
    platform: ConversationPlatform.EMAIL,
    icon: EmailIcon,
    label: "Email",
    route: "channels/email-connect",
  },
  {
    platform: ConversationPlatform.REDO_CHAT,
    icon: ChatIcon,
    label: "Chat",
    route: "customer-widget",
  },
  {
    platform: ConversationPlatform.VOICE,
    icon: PhoneIcon,
    label: "Phone",
    route: "channels/phone-setup",
  },
  {
    platform: ConversationPlatform.SMS,
    icon: MessageTextCircleIcon,
    label: "SMS",
    route: "channels/phone-setup",
  },
  {
    platform: ConversationPlatform.FACEBOOK,
    icon: FacebookIcon,
    label: "Facebook",
    route: "channels/meta-connect",
  },
  {
    platform: ConversationPlatform.INSTAGRAM,
    icon: InstagramIcon,
    label: "Instagram",
    route: "channels/meta-connect",
  },
  {
    platform: ConversationPlatform.ATTENTIVE,
    icon: AttentiveLogoIcon,
    label: "Attentive",
    route: "channels/attentive-connect",
  },
  {
    platform: ConversationPlatform.POSTSCRIPT,
    icon: PostscriptLogoIcon,
    label: "Postscript",
    route: "channels/postscript-connect",
  },
  {
    platform: ConversationPlatform.TIKTOK,
    icon: TikTokIcon,
    label: "TikTok",
    route: "channels/tiktok-settings",
  },
];

export const SupportOnboardingBanner = memo(function SupportOnboardingBanner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const team = useRequiredContext(TeamContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const [emailIntegrationsLoad] = useLazyContext(EmailIntegrationsContext);

  const integrationsLoad = useLoad(
    async (signal) => {
      if (!team) return null;

      const [facebook, instagram, postscript, attentive, tiktok] =
        await Promise.all([
          getFacebookIntegrations(client, { signal }),
          getInstagramIntegrations(client, { signal }),
          getPostscriptIntegration(client, { signal }),
          getAttentiveIntegration(client, { signal }),
          getTiktokIntegration(client, { signal }),
        ]);

      return { facebook, instagram, postscript, attentive, tiktok };
    },
    [team, client],
  );

  const isChannelConnected = (platform: ConversationPlatform): boolean => {
    switch (platform) {
      case ConversationPlatform.EMAIL:
        return (
          (emailIntegrationsLoad.value &&
            Object.values(emailIntegrationsLoad.value).some(
              (arr) => arr.length > 0,
            )) ||
          false
        );

      case ConversationPlatform.REDO_CHAT:
        return !!team.settings?.customerWidget;

      case ConversationPlatform.VOICE:
      case ConversationPlatform.SMS: {
        const voice = team.settings.support?.voice;
        const textMessaging =
          team.settings.support?.billing?.textMessaging?.enabled;
        const voiceNeedsSetup =
          !voice?.billingGroupId ||
          !voice?.outboundVoiceProfileId ||
          !voice?.sipConnectionCredential ||
          !voice?.sipConnectionId ||
          !voice?.voiceApplicationId;
        return !voiceNeedsSetup || !!textMessaging;
      }

      case ConversationPlatform.FACEBOOK:
      case ConversationPlatform.INSTAGRAM_COMMENTS:
      case ConversationPlatform.FACEBOOK_COMMENTS:
      case ConversationPlatform.INSTAGRAM:
        return integrationsLoad.value?.instagram?.integrations?.length > 0;

      case ConversationPlatform.ATTENTIVE:
        return integrationsLoad.value?.attentive?.integration?.purposes?.includes(
          AttentiveIntegrationPurpose.SUPPORT,
        );

      case ConversationPlatform.POSTSCRIPT:
        return !!integrationsLoad.value?.postscript?.integration;

      case ConversationPlatform.TIKTOK:
        return !!integrationsLoad.value?.tiktok?.integration;

      case ConversationPlatform.MOBILE_MESSAGE:
        return true;

      default:
        assertNever(platform);
    }
  };

  const handleDismiss = useHandler(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("showSupportOnboardingBanner");
    navigate({ search: newSearchParams.toString() }, { replace: true });
  });

  const handleChannelSetup = useHandler((channel: string) => {
    navigate(`/stores/${team._id}/settings/${channel}`);
  });

  return (
    <Flex className={supportOnboardingBannerCss.banner} dir="column">
      <Flex align="center" justify="space-between" mb="sm">
        <Flex align="center" gap="sm">
          <MessageTextSquare02
            className={supportOnboardingBannerCss.titleIcon}
          />
          <Text fontSize="md" fontWeight="semibold">
            Continue your set up
          </Text>
        </Flex>
        <RedoButton
          hierarchy="tertiary"
          IconLeading={XCloseIcon}
          onClick={handleDismiss}
        />
      </Flex>

      <Text color="tertiary" fontSize="sm" mb="md">
        Welcome to Redo's Support tool! Continue setting up your support
        channels now or do it later{" "}
        <a
          className={supportOnboardingBannerCss.hereLink}
          href={`/stores/${team._id}/settings/channels`}
          rel="noopener noreferrer"
          target="_blank"
        >
          here
        </a>
      </Text>

      <Flex gap="sm" wrap="wrap">
        {CHANNEL_CONFIGS.map((channel) => {
          const isConnected = isChannelConnected(channel.platform);
          return (
            <RedoButton
              disabled={isConnected}
              hierarchy="secondary"
              IconLeading={channel.icon}
              IconTrailing={
                isConnected
                  ? () => (
                      <CheckCircleIcon
                        className={supportOnboardingBannerCss.greenCheckmark}
                      />
                    )
                  : undefined
              }
              key={channel.platform}
              onClick={() => handleChannelSetup(channel.route)}
              size="sm"
              text={channel.label}
            />
          );
        })}
      </Flex>
    </Flex>
  );
});
